import {ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {RestUserService} from '@app/core/api/rest.user.service';
import {KontakteService} from '@app/core/state/kontakte';
import {PayloadUtil} from '@app/core/util/payload.util';
import {RightUtil} from '@app/core/util/right.util';
import {StaticPathUtil} from '@app/core/util/static-path.util';
import {StringUtil} from '@app/core/util/string.util';
import {ListEntry} from '@app/shared/list/list.component';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {KeyValue, Kontakt, PayloadBenutzerStatus, RightsConst} from '@pezi/entities';
import {BenutzerStatus} from '@pezi/entities/dist/src/main/ts/models/generated/person/benutzerstatus';
import {forkJoin, Observable} from 'rxjs';
import {map, take} from 'rxjs/operators';
import { NgIf, NgClass } from '@angular/common';
import { PersonContainerComponent } from '../partial-pages/person-container/person-container.component';
import { HasRightDirective } from '../directive/hasright.directive';
import { TextSmallDirective } from '../../core/directive/text-small.directive';
import { LoaderComponent } from '../loader/loader.component';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { PeziButtonComponent } from '../pezi-button/pezi-button.component';
import { ListComponent } from '../list/list.component';

@UntilDestroy()
@Component({
  selector: 'app-contact-detail',
  templateUrl: './contact-detail.component.html',
  styleUrls:  ['./contact-detail.component.scss'],
  standalone: true,
  imports:    [
    NgIf,
    PersonContainerComponent,
    HasRightDirective,
    TextSmallDirective,
    NgClass,
    LoaderComponent,
    MatDivider,
    MatIcon,
    PeziButtonComponent,
    ListComponent
  ]
})
export class ContactDetailComponent {

  @Input()
  set currentContact(value: Kontakt) {
    this._currentContact = value;
    if (value) {
      this.initKontakte();
      if (this.rightUtil.hasRight(RightsConst.PZ_ANWSTATUS)) {
        this.initCurrentOnlineStatus();
      }
    }
  }

  get currentContact(): Kontakt {
    return this._currentContact;
  }

  @Input()
  public showRelated = true;

  @Output()
  public onContactSelected = new EventEmitter<ListEntry<Kontakt>>();

  public allKontakte: Kontakt[];
  public mitarbeiter: ListEntry<Kontakt>[];
  public kollegen: ListEntry<Kontakt>[];
  public vorgesetzter: ListEntry<Kontakt>;
  public kollegenLoading = false;
  public mitarbeiterLoading = false;
  public vorgesetzterLoading = false;
  public currentDisplayedListIndex = 0;
  public currentBenutzerStatus: BenutzerStatus;
  public currentBenutzerStatusLoading = true;
  public benutzerStatusRight = RightsConst.PZ_ANWSTATUS;
  private _currentContact: Kontakt;

  constructor(private cd: ChangeDetectorRef, private kontakteService: KontakteService,
              private staticPath: StaticPathUtil, private userService: RestUserService,
              private rightUtil: RightUtil) {
  }


  public onContactSelect(kontaktEntry: ListEntry<Kontakt>): void {
    this._currentContact = kontaktEntry.data;
    this.initVorgesetzter(this._currentContact.useridLeiter);
    this.initMitarbeiter(this._currentContact.useridMitarbeiter);
    this.initKollegen(this._currentContact.useridKollegen);
    if (this.rightUtil.hasRight(RightsConst.PZ_ANWSTATUS)) {
      this.initCurrentOnlineStatus();
    }
    this.cd.markForCheck();
  }

  public openMail(mail: string): void {
    location.href = 'mailto:' + mail;
  }

  public openTel(mail: string): void {
    location.href = 'tel:' + mail;
  }

  public getAnwenwesenheitStr(bnStatus: BenutzerStatus): string {
    return StringUtil.getAnwenwesenheitStr(bnStatus);
  }

  private initCurrentOnlineStatus(): void {
    this.currentBenutzerStatus = null;
    this.currentBenutzerStatusLoading = true;
    this.userService.getStatusForUser(this.currentContact.userid)
      .pipe(take(1), untilDestroyed(this))
      .subscribe(status => {
        if (PayloadUtil.checkPayload(status)) {
          this.currentBenutzerStatus = status.data;
        }
        this.currentBenutzerStatusLoading = false;
        this.cd.markForCheck();
      });
  }

  private initMAWithOnlineStatus(userIds: string[], addToArray: ListEntry<Kontakt>[], resetLoadingKollegen: boolean): void {
    const statusObs$: Observable<KeyValue<string, PayloadBenutzerStatus>>[] = [];
    userIds.forEach(id => {
      statusObs$.push(this.userService.getStatusForUser(id)
        .pipe(map(res => ({
          key: id,
          value: res
        } as KeyValue<string, PayloadBenutzerStatus>))));
    });
    forkJoin(statusObs$)
      .pipe(untilDestroyed(this))
      .subscribe(statusResults => {
        statusResults.forEach(result => {
          const ma = this.allKontakte.find(v => v.userid === result.key);
          if (ma) {
            addToArray.push(this.getListEntryForMA(ma, PayloadUtil.checkPayload(result.value) ? result.value.data : null));
          }
        });
        if (resetLoadingKollegen) {
          this.kollegenLoading = false;
        } else {
          this.mitarbeiterLoading = false;
        }
        this.cd.markForCheck();
      });
  }

  private getListEntryForMA(contact: Kontakt, online?: BenutzerStatus): ListEntry<Kontakt> {
    return {
      text: StringUtil.getPersonDescWithTitle(contact),
      imageURL: this.staticPath.getPersonImgPath() + contact.image,
      subtext: StringUtil.getKontaktSubText(contact),
      additionalText: contact.abteilung,
      fallbackURL: this.staticPath.getFallbackPath(),
      data: contact,
      onlineStatus: online
    } as ListEntry<Kontakt>;
  }

  private initKontakte(): void {
    if (this._currentContact.useridLeiter || (this._currentContact.useridMitarbeiter && this._currentContact.useridMitarbeiter.length > 0)) {
      this.kontakteService.getKontakte()
        .pipe(
          take(1),
          untilDestroyed(this))
        .subscribe(v => {
          this.allKontakte = v;
          if (this.allKontakte) {
            this.initVorgesetzter(this._currentContact.useridLeiter);
            this.initMitarbeiter(this._currentContact.useridMitarbeiter);
            this.initKollegen(this._currentContact.useridKollegen);
          }
        });
    }
  }

  private initKollegen(userIds: string[]): void {
    this.kollegen = [];
    if (userIds && userIds.length > 0) {
      if (this.rightUtil.hasRight(RightsConst.PZ_ANWSTATUS)) {
        this.kollegenLoading = true;
        this.initMAWithOnlineStatus(userIds, this.kollegen, true);
      } else {
        userIds.forEach(id => {
          const ma = this.allKontakte.find(v => v.userid === id);
          if (ma) {
            this.kollegen.push(this.getListEntryForMA(ma));
          }
        });
      }
    }
    this.cd.markForCheck();
  }

  private initMitarbeiter(userIds: string[]): void {
    this.mitarbeiter = [];
    if (userIds && userIds.length > 0) {
      if (this.rightUtil.hasRight(RightsConst.PZ_ANWSTATUS)) {
        this.mitarbeiterLoading = true;
        this.initMAWithOnlineStatus(userIds, this.mitarbeiter, false);
      } else {
        userIds.forEach(id => {
          const ma = this.allKontakte.find(v => v.userid === id);
          if (ma) {
            this.mitarbeiter.push(this.getListEntryForMA(ma));
          }
        });
      }
    }
    this.cd.markForCheck();
  }

  private initVorgesetzter(userId: string): void {
    this.vorgesetzter = null;
    if (userId) {
      const vorges = this.allKontakte.find(k => k.userid === userId);
      if (vorges) {
        if (this.rightUtil.hasRight(RightsConst.PZ_ANWSTATUS)) {
          this.vorgesetzterLoading = true;
          this.userService.getStatusForUser(vorges.userid)
            .pipe(take(1), untilDestroyed(this))
            .subscribe(status => {
              this.vorgesetzter = this.getListEntryForMA(vorges, PayloadUtil.checkPayload(status) ? status.data : null);
              this.vorgesetzterLoading = false;
              this.cd.markForCheck();
            });
        } else {
          this.vorgesetzter = this.getListEntryForMA(vorges);
        }
      }
      this.cd.markForCheck();
    }
  }
}
